import type { ReactNode } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { FieldGroup } from '@launchpad-ui/components';

import { RequiredLabel } from '../../../CreateMetricModal/components/RequiredLabel';
import { MetricConfigRow } from '../components/MetricConfigRow';
import {
  ANALYSIS_TYPE_LABEL_ID,
  ANALYSIS_UNIT_LABEL_ID,
  PERCENTILE_LEVEL_LABEL_ID,
  SUCCESS_CRITERIA_LABEL_ID,
  UNIT_OF_MEASURE_LABEL_ID,
  UNITS_WITHOUT_EVENTS_LABEL_ID,
} from '../labels';

import styles from './SimpleMetricDefinitionSection.module.css';

interface SimpleMetricDefinitionSectionProps {
  id: string;
  errorMessage?: string;
  /**
   * Rows naming what to aggregate on, shown first in the row grid. They appear only for
   * value-type aggregations, so they sit inside the band — above it they would move the whole
   * band as the aggregation changed.
   */
  leadingRows?: ReactNode;
  /** The definition sentence: inline aggregation and analysis-type pickers plus unit tokens. */
  sentence: ReactNode;
  /** Absent when the data source does not support percentiles — then it is always Average. */
  analysisTypeControl?: ReactNode;
  percentileLevelControl?: ReactNode;
  analysisUnitControl?: ReactNode;
  /**
   * Rendered in the row's control column, beneath the picker. Kept outside the definition
   * FieldGroup so its invalid state doesn't reach the sentence's pickers.
   */
  analysisUnitError?: ReactNode;
  /** Absent for metric kinds that have no success-criteria choice. */
  successCriteriaControl?: ReactNode;
  unitsWithoutEventsControl?: ReactNode;
  unitOfMeasureControl?: ReactNode;
}

/**
 * Part 2 of the create-metric form: what the metric computes.
 *
 * Presentational only — every control arrives already rendered, so the pickers keep
 * the single instance, form registration, and validation wiring they have in the
 * flag-off layout.
 */
export const SimpleMetricDefinitionSection: React.FC<SimpleMetricDefinitionSectionProps> = ({
  id,
  errorMessage,
  leadingRows,
  sentence,
  analysisTypeControl,
  percentileLevelControl,
  analysisUnitControl,
  analysisUnitError,
  successCriteriaControl,
  unitsWithoutEventsControl,
  unitOfMeasureControl,
}) => (
  <div className={styles.definitionSection}>
    <FieldGroup errorMessage={errorMessage}>
      <RequiredLabel label={msg('metrics.metricDefinition.definitionLabel')} htmlFor={id} />
      <div className={styles.definitionBox} id={id}>
        <span className={styles.definitionSentence}>{sentence}</span>
      </div>
    </FieldGroup>

    {/* One grid so the label column sizes to the longest label and every control lines up. */}
    <div className={styles.formRows}>
      {leadingRows}
      {analysisTypeControl && (
        <MetricConfigRow
          inGrid
          label={msg('metrics.metricDefinition.analysisTypeLabel')}
          labelId={ANALYSIS_TYPE_LABEL_ID}
        >
          {analysisTypeControl}
        </MetricConfigRow>
      )}
      {percentileLevelControl && (
        <MetricConfigRow
          inGrid
          isRequired
          label={msg('metrics.metricDefinition.percentileLevelLabel')}
          labelId={PERCENTILE_LEVEL_LABEL_ID}
        >
          {percentileLevelControl}
        </MetricConfigRow>
      )}
      {analysisUnitControl && (
        <MetricConfigRow
          inGrid
          label={msg('metrics.metricDefinition.analysisUnitLabel')}
          labelId={ANALYSIS_UNIT_LABEL_ID}
          error={analysisUnitError}
        >
          {analysisUnitControl}
        </MetricConfigRow>
      )}
      {successCriteriaControl && (
        <MetricConfigRow
          inGrid
          label={msg('metrics.metricDefinition.successCriteriaLabel')}
          labelId={SUCCESS_CRITERIA_LABEL_ID}
        >
          {successCriteriaControl}
        </MetricConfigRow>
      )}
      {unitsWithoutEventsControl && (
        <MetricConfigRow
          inGrid
          label={msg('metrics.metricDefinition.unitsWithoutEventsLabel')}
          labelId={UNITS_WITHOUT_EVENTS_LABEL_ID}
        >
          {unitsWithoutEventsControl}
        </MetricConfigRow>
      )}
      {unitOfMeasureControl && (
        <MetricConfigRow
          inGrid
          label={msg('metrics.metricForm.unitLabel')}
          labelId={UNIT_OF_MEASURE_LABEL_ID}
          htmlFor="unit"
        >
          {unitOfMeasureControl}
        </MetricConfigRow>
      )}
    </div>
  </div>
);
